import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { actionItemAPI, userAPI, meetingAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Search, Plus, Loader2, CheckSquare, Calendar,
  User, AlertTriangle, Users, Briefcase, Building2, UserCircle
} from 'lucide-react';
import { format, parseISO, isPast, isToday, addDays, isBefore } from 'date-fns';
import { toast } from 'sonner';

const RESPONSIBLE_GROUPS = [
  { value: 'secretariat', label: 'Secretariat Team', icon: Briefcase, color: 'bg-teal-50 border-teal-200 text-teal-800' },
  { value: 'board_committee', label: 'Board / Committees', icon: Users, color: 'bg-blue-50 border-blue-200 text-blue-800' },
  { value: 'other_officials', label: 'Other Officials', icon: Building2, color: 'bg-purple-50 border-purple-200 text-purple-800' },
  { value: 'individual', label: 'Individual Person', icon: UserCircle, color: 'bg-amber-50 border-amber-200 text-amber-800' },
];

const ActionItems = () => {
  const { user } = useAuth();
  const [actionItems, setActionItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [viewTab, setViewTab] = useState('grouped');
  const [addDialog, setAddDialog] = useState(false);
  const [newItem, setNewItem] = useState({
    description: '', responsible_group: 'secretariat', owner_id: '', responsible_name: '',
    due_date: '', priority: 'medium', office: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [itemsRes, usersRes] = await Promise.all([
        actionItemAPI.getAll(params),
        userAPI.getAll(),
      ]);
      setActionItems(itemsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally { setLoading(false); }
  };

  const getUserName = (userId) => users.find(u => u.id === userId)?.full_name || 'Unknown';

  const getStatusColor = (status) => ({
    pending: 'bg-amber-100 text-amber-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-600',
  }[status] || 'bg-gray-100 text-gray-700');

  const getPriorityColor = (priority) => ({
    low: 'bg-gray-100 text-gray-600',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700',
  }[priority] || 'bg-gray-100 text-gray-600');

  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return 'normal';
    const date = parseISO(dueDate);
    if (isPast(date) && !isToday(date)) return 'overdue';
    if (isToday(date)) return 'due-today';
    if (isBefore(date, addDays(new Date(), 3))) return 'due-soon';
    return 'normal';
  };

  const getDueDateColor = (dueDate) => ({
    overdue: 'text-red-600', 'due-today': 'text-orange-600',
    'due-soon': 'text-amber-600', normal: 'text-[#5A6570]',
  }[getDueDateStatus(dueDate)] || 'text-[#5A6570]');

  const handleStatusToggle = async (itemId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await actionItemAPI.update(itemId, { status: newStatus });
      setActionItems(items => items.map(item => item.id === itemId ? { ...item, status: newStatus } : item));
      toast.success(newStatus === 'completed' ? 'Marked complete' : 'Reopened');
    } catch (err) { toast.error('Failed to update'); }
  };

  const handleCreate = async () => {
    if (!newItem.description || !newItem.due_date) { toast.error('Description and due date are required'); return; }
    setSaving(true);
    try {
      const payload = {
        description: newItem.description,
        due_date: new Date(newItem.due_date).toISOString(),
        priority: newItem.priority,
        responsible_group: newItem.responsible_group,
        organization_id: user?.organization_id,
      };
      if (newItem.responsible_group === 'individual') {
        if (newItem.owner_id) payload.owner_id = newItem.owner_id;
        if (newItem.responsible_name) payload.responsible_name = newItem.responsible_name;
      } else {
        if (newItem.owner_id) payload.owner_id = newItem.owner_id;
      }
      if (newItem.office) payload.office = newItem.office;

      const res = await actionItemAPI.create(payload);
      setActionItems([...actionItems, res.data]);
      setAddDialog(false);
      setNewItem({ description: '', responsible_group: 'secretariat', owner_id: '', responsible_name: '', due_date: '', priority: 'medium', office: '' });
      toast.success('Action item created');
    } catch (err) { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  let filteredItems = actionItems.filter(item =>
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  if (priorityFilter !== 'all') filteredItems = filteredItems.filter(item => item.priority === priorityFilter);

  const pendingItems = filteredItems.filter(i => i.status !== 'completed' && i.status !== 'cancelled');
  const completedItems = filteredItems.filter(i => i.status === 'completed');

  const getResponsibleDisplay = (item) => {
    if (item.responsible_name) return item.responsible_name;
    if (item.owner_id) return getUserName(item.owner_id);
    return 'Unassigned';
  };

  const getGroupForItem = (item) => {
    if (item.responsible_group) return item.responsible_group;
    if (item.owner_id) return 'individual';
    return 'secretariat';
  };

  const ItemRow = ({ item }) => (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E6EA] hover:border-[#00A3A3]/50 transition-colors bg-white" data-testid={`action-item-${item.id}`}>
      <Checkbox
        checked={item.status === 'completed'}
        onCheckedChange={() => handleStatusToggle(item.id, item.status)}
        className="mt-0.5 border-[#00A3A3] data-[state=checked]:bg-[#00A3A3]"
        data-testid={`action-item-check-${item.id}`}
      />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${item.status === 'completed' ? 'line-through text-[#A8B1B9]' : 'text-[#1A1D21]'}`}>
          {item.description}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <Badge variant="outline" className={`text-[10px] py-0 ${getPriorityColor(item.priority)}`}>{item.priority}</Badge>
          <span className="text-xs text-[#5A6570] flex items-center gap-1">
            <User className="h-3 w-3" />{getResponsibleDisplay(item)}
          </span>
          {item.due_date && (
            <span className={`text-xs flex items-center gap-1 ${getDueDateColor(item.due_date)}`}>
              {getDueDateStatus(item.due_date) === 'overdue' && <AlertTriangle className="h-3 w-3" />}
              <Calendar className="h-3 w-3" />
              {format(parseISO(item.due_date), 'MMM d, yyyy')}
              {getDueDateStatus(item.due_date) === 'overdue' && item.status !== 'completed' && ' (Overdue)'}
            </span>
          )}
          {item.office && <span className="text-[10px] text-[#5A6570] bg-[#F0F2F4] px-1.5 py-0.5 rounded">{item.office}</span>}
        </div>
      </div>
      <Badge variant="outline" className={`text-[10px] shrink-0 ${getStatusColor(item.status)}`}>
        {item.status?.replace('_', ' ')}
      </Badge>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" /></div>;

  return (
    <div className="space-y-6" data-testid="action-items-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1D21]">Tasks</h1>
          <p className="text-[#5A6570] mt-1">Track and manage tasks from meetings and resolutions</p>
        </div>
        <Button onClick={() => setAddDialog(true)} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="create-action-item-btn">
          <Plus className="h-4 w-4 mr-2" />New Action Item
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#E2E6EA]"><CardContent className="p-4">
          <p className="text-sm text-[#5A6570]">Total</p>
          <p className="text-2xl font-bold text-[#1A1D21]">{filteredItems.length}</p>
        </CardContent></Card>
        <Card className="border-[#E2E6EA]"><CardContent className="p-4">
          <p className="text-sm text-[#5A6570]">Pending</p>
          <p className="text-2xl font-bold text-amber-600">{pendingItems.length}</p>
        </CardContent></Card>
        <Card className="border-[#E2E6EA]"><CardContent className="p-4">
          <p className="text-sm text-[#5A6570]">Completed</p>
          <p className="text-2xl font-bold text-green-600">{completedItems.length}</p>
        </CardContent></Card>
        <Card className="border-[#E2E6EA]"><CardContent className="p-4">
          <p className="text-sm text-[#5A6570]">Overdue</p>
          <p className="text-2xl font-bold text-red-600">{pendingItems.filter(i => getDueDateStatus(i.due_date) === 'overdue').length}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
          <Input placeholder="Search action items..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="pl-10 border-[#E2E6EA]" data-testid="search-action-items-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-[150px] border-[#E2E6EA]" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-full md:w-[150px] border-[#E2E6EA]" data-testid="priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* View Tabs */}
      <Tabs value={viewTab} onValueChange={setViewTab}>
        <TabsList className="bg-[#F0F2F4] p-1">
          <TabsTrigger value="grouped" className="data-[state=active]:bg-white text-xs" data-testid="tab-grouped">By Responsible Party</TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-white text-xs" data-testid="tab-pending">Open Tasks</TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-white text-xs" data-testid="tab-completed">Completed</TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-white text-xs" data-testid="tab-all">All</TabsTrigger>
        </TabsList>

        {/* Grouped View */}
        <TabsContent value="grouped" className="mt-4">
          {filteredItems.length === 0 ? (
            <EmptyState searchTerm={searchTerm} />
          ) : (
            <div className="space-y-6">
              {RESPONSIBLE_GROUPS.map(group => {
                const groupItems = pendingItems.filter(i => getGroupForItem(i) === group.value);
                if (groupItems.length === 0) return null;
                const GroupIcon = group.icon;
                return (
                  <div key={group.value} data-testid={`group-${group.value}`}>
                    <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg border ${group.color}`}>
                      <GroupIcon className="h-4 w-4" />
                      <h3 className="text-sm font-semibold">{group.label}</h3>
                      <Badge variant="outline" className="ml-auto text-[10px]">{groupItems.length}</Badge>
                    </div>
                    <div className="space-y-2 pl-2">
                      {groupItems.map(item => <ItemRow key={item.id} item={item} />)}
                    </div>
                  </div>
                );
              })}
              {/* Items without a group */}
              {(() => {
                const ungrouped = pendingItems.filter(i => !RESPONSIBLE_GROUPS.some(g => g.value === getGroupForItem(i)));
                if (ungrouped.length === 0) return null;
                return (
                  <div data-testid="group-unassigned">
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg border bg-gray-50 border-gray-200 text-gray-700">
                      <CheckSquare className="h-4 w-4" />
                      <h3 className="text-sm font-semibold">Unassigned</h3>
                      <Badge variant="outline" className="ml-auto text-[10px]">{ungrouped.length}</Badge>
                    </div>
                    <div className="space-y-2 pl-2">
                      {ungrouped.map(item => <ItemRow key={item.id} item={item} />)}
                    </div>
                  </div>
                );
              })()}
              {completedItems.length > 0 && (
                <div data-testid="group-completed">
                  <h3 className="text-sm font-semibold text-[#A8B1B9] mb-3 mt-4">Completed ({completedItems.length})</h3>
                  <div className="space-y-2 pl-2 opacity-70">
                    {completedItems.slice(0, 5).map(item => <ItemRow key={item.id} item={item} />)}
                    {completedItems.length > 5 && <p className="text-xs text-[#A8B1B9] pl-3">+{completedItems.length - 5} more completed items</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Pending View */}
        <TabsContent value="pending" className="mt-4">
          {pendingItems.length === 0 ? <EmptyState searchTerm={searchTerm} /> : (
            <div className="space-y-2">{pendingItems.map(item => <ItemRow key={item.id} item={item} />)}</div>
          )}
        </TabsContent>

        {/* Completed View */}
        <TabsContent value="completed" className="mt-4">
          {completedItems.length === 0 ? <EmptyState searchTerm={searchTerm} message="No completed items" /> : (
            <div className="space-y-2">{completedItems.map(item => <ItemRow key={item.id} item={item} />)}</div>
          )}
        </TabsContent>

        {/* All View */}
        <TabsContent value="all" className="mt-4">
          {filteredItems.length === 0 ? <EmptyState searchTerm={searchTerm} /> : (
            <div className="space-y-2">{filteredItems.map(item => <ItemRow key={item.id} item={item} />)}</div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Action Item Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New Action Item</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Description</Label>
              <Textarea value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Describe the task..." className="mt-1 border-[#E2E6EA] min-h-[80px]" data-testid="new-ai-description" />
            </div>
            <div>
              <Label>Responsible Party</Label>
              <Select value={newItem.responsible_group} onValueChange={v => setNewItem({ ...newItem, responsible_group: v })}>
                <SelectTrigger className="mt-1 border-[#E2E6EA]" data-testid="new-ai-group"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RESPONSIBLE_GROUPS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {newItem.responsible_group === 'individual' && (
              <div className="space-y-3">
                <div>
                  <Label>Assign to (System User)</Label>
                  <Select value={newItem.owner_id} onValueChange={v => setNewItem({ ...newItem, owner_id: v })}>
                    <SelectTrigger className="mt-1 border-[#E2E6EA]" data-testid="new-ai-owner"><SelectValue placeholder="Select user (optional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Or External Person Name</Label>
                  <Input value={newItem.responsible_name} onChange={e => setNewItem({ ...newItem, responsible_name: e.target.value })}
                    placeholder="e.g., Atty. Juan Dela Cruz (External Counsel)" className="mt-1 border-[#E2E6EA]" data-testid="new-ai-responsible-name" />
                  <p className="text-[10px] text-[#A8B1B9] mt-0.5">For persons outside the system</p>
                </div>
              </div>
            )}
            {newItem.responsible_group !== 'individual' && (
              <div>
                <Label>Assigned To (Optional)</Label>
                <Select value={newItem.owner_id} onValueChange={v => setNewItem({ ...newItem, owner_id: v })}>
                  <SelectTrigger className="mt-1 border-[#E2E6EA]"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={newItem.due_date} onChange={e => setNewItem({ ...newItem, due_date: e.target.value })}
                  className="mt-1 border-[#E2E6EA]" data-testid="new-ai-due-date" />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={newItem.priority} onValueChange={v => setNewItem({ ...newItem, priority: v })}>
                  <SelectTrigger className="mt-1 border-[#E2E6EA]" data-testid="new-ai-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Office / Department (Optional)</Label>
              <Input value={newItem.office} onChange={e => setNewItem({ ...newItem, office: e.target.value })}
                placeholder="e.g., Legal, Finance" className="mt-1 border-[#E2E6EA]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-action-item-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EmptyState = ({ searchTerm, message }) => (
  <Card className="border-[#E2E6EA]">
    <CardContent className="py-12 text-center">
      <CheckSquare className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
      <h3 className="text-base font-medium text-[#1A1D21] mb-1">{message || 'No action items found'}</h3>
      <p className="text-sm text-[#5A6570]">{searchTerm ? 'Try adjusting your search or filters' : 'Action items are created from meetings or manually'}</p>
    </CardContent>
  </Card>
);

export default ActionItems;
